/**
 * DysonSphere — CSS-3D brand mark.
 *
 * A real 3D sphere built from layered planes that tumble in
 * perspective:
 *
 *   • Wireframe globe — 12 meridians + 5 latitudes (dashed, faint)
 *   • 3 prominent orbital rings (tilted on different axes,
 *     each with stations along the ring)
 *   • Atmospheric halo + corona behind the scene
 *   • A flat-on-camera star core that the rings occlude properly
 *     thanks to preserve-3d depth sorting
 *
 * Real depth: each ring is a CSS plane in 3D space, so as the
 * sphere tumbles, the back hemisphere of each ring is naturally
 * hidden behind the star and the front hemisphere passes in
 * front. No SVG-only tricks.
 *
 * Behavioural variants are still driven by the ``mode`` prop so
 * callers (sidebar, splash, voice orb) don't have to rewire — the
 * mode adjusts tumble speed, core pulse intensity, and halo glow.
 *
 * | mode          | tumble       | core pulse              | halo |
 * |---------------|--------------|-------------------------|------|
 * | ``idle``      | slow         | gentle warm breathe     | low  |
 * | ``thinking``  | medium       | cool accent pulse       | med  |
 * | ``listening`` | fast         | blazing warm pulse      | high |
 * | ``speaking``  | fast         | blazing warm pulse      | high |
 * | ``splash``    | very fast    | maximum bloom + grow    | max  |
 *
 * Spec: Docs/architecture/24-omni-pane.md (sphere is the brand
 *       primitive); Docs/design/12-logo-and-wordmark.md.
 */
import * as React from "react";
import { cn } from "@/lib/cn";

export type DysonMode =
  | "idle"
  | "thinking"
  | "listening"
  | "speaking"
  | "splash";

export interface DysonSphereProps {
  /** Behavioural variant — see the table in the file header. */
  mode?: DysonMode;
  /** Optional tailwind classes for the wrapper element. */
  className?: string;
  /** When true: idle becomes listening on hover. Default false. */
  hoverActivates?: boolean;
  /**
   * Ambient glow intensity (0–1). Scales the outer halo without
   * changing the sphere itself. Useful for mic-volume feedback on
   * the voice orb and for the splash bloom ramp.
   */
  glow?: number;
  /**
   * Inline style overrides (for e.g. ``transform: scale(...)``
   * driven by parent-level intro animations).
   */
  style?: React.CSSProperties;
  /**
   * Pixel size of the bounding box. Defaults to filling the
   * parent's width. Used by the sidebar / nav variants where the
   * mark needs a hard size lock.
   */
  size?: number;
  /**
   * Compact mode — switches to a flat SVG render at small sizes
   * (< 32 px) where the 3D scene doesn't have room to read. Lets
   * the same component serve nav (22px) + splash (200px).
   */
  variant?: "auto" | "full" | "mini";
}

// Per-mode tuning knobs. Speed values are CSS animation durations.
const MODE_TUNE: Record<
  DysonMode,
  { tumbleS: number; spin0S: number; spin1S: number; spin2S: number; haloOpacity: number; corePulse: "gentle" | "med" | "hot" | "max" }
> = {
  idle:      { tumbleS: 36, spin0S: 20, spin1S: 28, spin2S: 36, haloOpacity: 0.55, corePulse: "gentle" },
  thinking:  { tumbleS: 18, spin0S: 12, spin1S: 18, spin2S: 24, haloOpacity: 0.7,  corePulse: "med"    },
  listening: { tumbleS: 10, spin0S:  7, spin1S: 11, spin2S: 16, haloOpacity: 0.95, corePulse: "hot"    },
  speaking:  { tumbleS: 10, spin0S:  7, spin1S: 11, spin2S: 16, haloOpacity: 0.95, corePulse: "hot"    },
  splash:    { tumbleS:  6, spin0S:  5, spin1S:  8, spin2S: 12, haloOpacity: 1.0,  corePulse: "max"    },
};

function DysonSphereImpl({
  mode = "idle",
  className,
  hoverActivates = false,
  glow = 1,
  style,
  size,
  variant = "auto",
}: DysonSphereProps) {
  const [hovered, setHovered] = React.useState(false);
  const effectiveMode: DysonMode =
    hoverActivates && hovered && mode === "idle" ? "listening" : mode;

  // Decide variant — "auto" downshifts to the flat mini at < 32 px
  // because the 3D scene needs roughly 60 px of room to read.
  const useMini =
    variant === "mini" ||
    (variant === "auto" && typeof size === "number" && size < 32);

  if (useMini) {
    return (
      <DysonMini
        className={className}
        style={style}
        size={size}
        mode={effectiveMode}
      />
    );
  }

  return (
    <DysonFull
      className={className}
      style={style}
      size={size}
      glow={glow}
      mode={effectiveMode}
      onHover={setHovered}
    />
  );
}

/**
 * Memoised so the voice orb's ~60 fps amplitude pulse (which re-renders
 * the parent every frame) does NOT reconcile this ~20-SVG 3D scene each
 * time — its tumble/spin/core/halo animations are pure CSS keyframes on
 * the GPU compositor and need no React involvement. We re-render only on
 * a mode change or a perceptible ``glow`` step (the halo opacity), which
 * caps reconciliations at a handful per second instead of 60 with no
 * visible change.
 */
export const DysonSphere = React.memo(
  DysonSphereImpl,
  (a, b) =>
    a.mode === b.mode &&
    a.variant === b.variant &&
    a.size === b.size &&
    a.className === b.className &&
    a.hoverActivates === b.hoverActivates &&
    a.style === b.style &&
    Math.abs((a.glow ?? 1) - (b.glow ?? 1)) < 0.06,
);

// ─── Full 3D variant ────────────────────────────────────────────────────

function DysonFull({
  className,
  style,
  size,
  glow,
  mode,
  onHover,
}: {
  className?: string;
  style?: React.CSSProperties;
  size?: number;
  glow: number;
  mode: DysonMode;
  onHover: (v: boolean) => void;
}) {
  const tune = MODE_TUNE[mode];
  // Unique id suffix so multiple spheres on one page don't share
  // keyframe namespaces if keyframes ever become id-scoped (they
  // aren't today, but the cost is one ``id`` lookup).
  const uid = React.useId().replace(/:/g, "");

  // Inline CSS — the 3D transforms, multiple spinning rings, halo,
  // and core-pulse keyframes don't have a clean Tailwind expression.
  // Scoped via `data-uid` so multiple instances don't collide.
  const sizePx = size ? `${size}px` : "100%";
  return (
    <span
      className={cn(
        "relative inline-block",
        "[--dy-color-accent:var(--om-color-accent)]",
        "[--dy-color-fg:var(--om-color-fg)]",
        className,
      )}
      style={{
        width: sizePx,
        aspectRatio: "1 / 1",
        // The sphere uses oklch-defined accent + fg color tokens
        // exposed via CSS vars so light/dark switches automatically.
        perspective: size ? `${size * 6}px` : "1200px",
        ...style,
      }}
      data-dyson-uid={uid}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      role="img"
      aria-label="Omni"
    >
      <style>{dysonInlineCss(uid, tune)}</style>

      {/* Outer atmospheric halo — sits BEHIND the 3D scene */}
      <span
        className="dy-halo"
        style={{ opacity: Math.max(0, Math.min(1, glow)) * tune.haloOpacity }}
      />

      <span className="dy-scene">
        {/* Tumbling sphere — wireframe + orbital rings */}
        <span className="dy-sphere">
          {/* Meridians */}
          {MERIDIANS.map((rotY, i) => (
            <span
              key={`m-${i}`}
              className="dy-plane dy-meridian"
              style={{ transform: `rotateY(${rotY}deg)` }}
            >
              <Wire />
            </span>
          ))}

          {/* Latitudes */}
          {LATITUDES.map((h, i) => {
            const r = Math.sqrt(1 - h * h);
            return (
              <span
                key={`l-${i}`}
                className="dy-plane dy-latitude"
                style={{
                  transform: `translate3d(0, ${(-h * 100) / 2}%, 0) rotateX(90deg) scale(${r})`,
                }}
              >
                <Wire />
              </span>
            );
          })}

          {/* Three prominent orbital rings, each on its own plane */}
          {ORBITS.map((o, i) => (
            <span
              key={`o-${i}`}
              className={`dy-plane dy-orbit dy-orbit--${i}`}
            >
              <OrbitWire color={o.color} stations={o.stations} />
            </span>
          ))}
        </span>

        {/* Star core — at z=0 inside the 3D scene so the rings
            depth-sort in front of / behind it. */}
        <span className="dy-core" />
        <span className="dy-core-hot" />

        {/* Lens-flare cross — fixed to the camera */}
        <span className="dy-flare" aria-hidden>
          <span className="dy-flare-h" />
          <span className="dy-flare-v" />
        </span>
      </span>
    </span>
  );
}

// ─── Mini 2D variant ────────────────────────────────────────────────────
// Used in nav / footer where the 3D scene doesn't have room to read.
// Two horizontal-axis rings + a glowing core dot. Kept as a single SVG
// so it survives at 16-22 px without aliasing.

function DysonMini({
  className,
  style,
  size,
  mode,
}: {
  className?: string;
  style?: React.CSSProperties;
  size?: number;
  mode: DysonMode;
}) {
  const sizePx = size ?? 22;
  // Mini variant doesn't tumble — the rings are static at the same
  // tilts as the full sphere's three orbits, viewed roughly head-on.
  const corePulse = mode === "idle" ? "om-dyson-pulse-gentle" : "om-dyson-pulse-hot";
  const uid = React.useId().replace(/:/g, "");

  return (
    <span
      className={cn("relative inline-block text-fg", className)}
      style={{ width: sizePx, height: sizePx, ...style }}
      role="img"
      aria-label="Omni"
    >
      <style>{`
        @keyframes om-dyson-pulse-gentle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.92; transform: scale(1.06); }
        }
        @keyframes om-dyson-pulse-hot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.18); }
        }
      `}</style>
      <svg viewBox="-100 -100 200 200" width="100%" height="100%">
        <defs>
          <radialGradient id={`mini-${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="white" stopOpacity="1" />
            <stop offset="55%" stopColor="var(--om-color-accent)" stopOpacity="1" />
            <stop offset="100%" stopColor="var(--om-color-accent)" stopOpacity="0" />
          </radialGradient>
        </defs>
        <ellipse cx="0" cy="0" rx="86" ry="86" fill="none"
          stroke="currentColor" strokeOpacity="0.55"
          strokeWidth="3" strokeDasharray="0.8 4" />
        <ellipse cx="0" cy="0" rx="86" ry="34" fill="none"
          stroke="currentColor" strokeOpacity="0.9"
          strokeWidth="3" strokeDasharray="0.8 4"
          transform="rotate(-22)" />
        <ellipse cx="0" cy="0" rx="86" ry="48" fill="none"
          stroke="currentColor" strokeOpacity="0.7"
          strokeWidth="3" strokeDasharray="0.8 4"
          transform="rotate(28)" />
        <g style={{
          transformOrigin: "center",
          animation: `${corePulse} 2.4s ease-in-out infinite`,
        }}>
          <circle cx="0" cy="0" r="20" fill={`url(#mini-${uid})`} />
          <circle cx="0" cy="0" r="6" fill="white" />
        </g>
      </svg>
    </span>
  );
}

// ─── Wire + orbit primitives ────────────────────────────────────────────

function Wire() {
  return (
    <svg
      viewBox="-100 -100 200 200"
      className="absolute inset-0 h-full w-full overflow-visible text-fg"
    >
      <ellipse
        cx="0" cy="0" rx="80" ry="80"
        fill="none" stroke="currentColor"
        strokeWidth="0.7" strokeOpacity="0.22"
        strokeDasharray="0.6 2.6" strokeLinecap="round"
      />
    </svg>
  );
}

function OrbitWire({ color, stations }: { color: "accent" | "fg"; stations: number }) {
  return (
    <svg
      viewBox="-100 -100 200 200"
      className="absolute inset-0 h-full w-full overflow-visible text-fg"
    >
      {/* Substantial ring band — the orbital ring needs to read as a
          real megastructure, not a wireframe. strokeWidth bumped to
          4.5 (from 2.2) and the dash gap widened so the discrete
          stations still register. */}
      <ellipse
        cx="0" cy="0" rx="92" ry="92"
        fill="none"
        stroke={color === "accent" ? "var(--om-color-accent)" : "currentColor"}
        strokeOpacity={color === "accent" ? 0.95 : 0.75}
        strokeWidth="4.5"
        strokeDasharray="1.6 4.2"
        strokeLinecap="round"
      />
      {Array.from({ length: stations }).map((_, s) => {
        const a = (s * 360) / stations;
        const x = Math.cos((a * Math.PI) / 180) * 92;
        const y = Math.sin((a * Math.PI) / 180) * 92;
        return (
          <circle
            key={s}
            cx={x} cy={y} r={s === 0 ? 2.6 : 1.7}
            fill={s === 0 ? "white" : "var(--om-color-accent)"}
          />
        );
      })}
    </svg>
  );
}

// ─── geometry constants ─────────────────────────────────────────────────

const MERIDIANS: number[] = Array.from(
  { length: 12 },
  (_, i) => (i * 180) / 12,
);

const LATITUDES: number[] = [-0.78, -0.5, -0.22, 0.22, 0.5, 0.78];

interface OrbitDef {
  color: "accent" | "fg";
  stations: number;
}
const ORBITS: OrbitDef[] = [
  { color: "accent", stations: 6 },
  { color: "fg", stations: 6 },
  { color: "fg", stations: 4 },
];

// ─── inline CSS generator ───────────────────────────────────────────────

function dysonInlineCss(
  uid: string,
  tune: typeof MODE_TUNE[DysonMode],
): string {
  // Scope rules with [data-dyson-uid="…"] so multiple spheres on the
  // same page don't fight over the same class names. The keyframes
  // themselves are namespaced by uid for the same reason.
  const sel = `[data-dyson-uid="${uid}"]`;
  return `
    ${sel} { isolation: isolate; color: var(--om-color-fg); }
    ${sel} .dy-halo {
      position: absolute; inset: -20%; z-index: -1;
      background: radial-gradient(circle, color-mix(in oklab, var(--om-color-accent) 45%, transparent) 0%, transparent 55%);
      filter: blur(6%);
      animation: dy-halo-${uid} 5s ease-in-out infinite;
      pointer-events: none;
    }
    ${sel} .dy-scene {
      position: absolute; inset: 0; transform-style: preserve-3d;
    }
    ${sel} .dy-sphere {
      position: absolute; inset: 0; transform-style: preserve-3d;
      animation: dy-tumble-${uid} ${tune.tumbleS}s linear infinite;
    }
    @keyframes dy-tumble-${uid} {
      from { transform: rotateY(0deg)   rotateX(-12deg); }
      to   { transform: rotateY(360deg) rotateX(-12deg); }
    }
    ${sel} .dy-plane {
      position: absolute; inset: 0;
      transform-style: preserve-3d;
      pointer-events: none;
    }
    ${sel} .dy-orbit--0 {
      animation: dy-spin-0-${uid} ${tune.spin0S}s linear infinite;
    }
    ${sel} .dy-orbit--1 {
      animation: dy-spin-1-${uid} ${tune.spin1S}s linear infinite;
    }
    ${sel} .dy-orbit--2 {
      animation: dy-spin-2-${uid} ${tune.spin2S}s linear infinite;
    }
    @keyframes dy-spin-0-${uid} {
      from { transform: rotateX(76deg) rotateY(0deg)   rotateZ(0deg); }
      to   { transform: rotateX(76deg) rotateY(360deg) rotateZ(0deg); }
    }
    @keyframes dy-spin-1-${uid} {
      from { transform: rotateX(20deg) rotateY(0deg)    rotateZ(58deg); }
      to   { transform: rotateX(20deg) rotateY(-360deg) rotateZ(58deg); }
    }
    @keyframes dy-spin-2-${uid} {
      from { transform: rotateX(50deg) rotateY(0deg)   rotateZ(-32deg); }
      to   { transform: rotateX(50deg) rotateY(360deg) rotateZ(-32deg); }
    }
    ${sel} .dy-core,
    ${sel} .dy-core-hot {
      position: absolute; left: 50%; top: 50%;
      border-radius: 50%;
      pointer-events: none;
      transform: translate3d(-50%, -50%, 0);
    }
    ${sel} .dy-core {
      width: 32%; height: 32%;
      background: radial-gradient(circle,
        white 0%,
        white 8%,
        var(--om-color-accent) 30%,
        color-mix(in oklab, var(--om-color-accent), transparent 40%) 55%,
        transparent 75%);
      filter: blur(1.2%);
      box-shadow:
        0 0 10% var(--om-color-accent),
        0 0 22% color-mix(in oklab, var(--om-color-accent) 45%, transparent);
      animation: dy-pulse-${tune.corePulse}-${uid} 3s ease-in-out infinite;
      z-index: 0;
    }
    ${sel} .dy-core-hot {
      width: 9%; height: 9%;
      background: radial-gradient(circle, white 0%, white 60%, transparent 100%);
      box-shadow: 0 0 4% white, 0 0 8% white;
      animation: dy-hot-${uid} 2s ease-in-out infinite;
      z-index: 1;
    }
    @keyframes dy-pulse-gentle-${uid} {
      0%, 100% { transform: translate(-50%, -50%) scale(1);    opacity: 1; }
      50%      { transform: translate(-50%, -50%) scale(1.06); opacity: 0.94; }
    }
    @keyframes dy-pulse-med-${uid} {
      0%, 100% { transform: translate(-50%, -50%) scale(1);    opacity: 1; }
      50%      { transform: translate(-50%, -50%) scale(1.10); opacity: 0.9; }
    }
    @keyframes dy-pulse-hot-${uid} {
      0%, 100% { transform: translate(-50%, -50%) scale(1);    opacity: 1; }
      50%      { transform: translate(-50%, -50%) scale(1.20); opacity: 0.85; }
    }
    @keyframes dy-pulse-max-${uid} {
      0%, 100% { transform: translate(-50%, -50%) scale(1);    opacity: 1; }
      50%      { transform: translate(-50%, -50%) scale(1.30); opacity: 0.8; }
    }
    @keyframes dy-hot-${uid} {
      0%, 100% { transform: translate(-50%, -50%) scale(1);    opacity: 1; }
      50%      { transform: translate(-50%, -50%) scale(1.25); opacity: 0.85; }
    }
    @keyframes dy-halo-${uid} {
      0%, 100% { opacity: ${tune.haloOpacity * 0.65}; transform: scale(1); }
      50%      { opacity: ${tune.haloOpacity}; transform: scale(1.04); }
    }
    ${sel} .dy-flare {
      position: absolute; left: 50%; top: 50%;
      width: 60%; height: 60%;
      transform: translate(-50%, -50%);
      pointer-events: none;
      opacity: 0.55;
      z-index: 2;
    }
    ${sel} .dy-flare-h, ${sel} .dy-flare-v {
      position: absolute; left: 50%; top: 50%;
    }
    ${sel} .dy-flare-h {
      width: 100%; height: 1px;
      transform: translate(-50%, -50%);
      background: linear-gradient(to right,
        transparent 0%, rgba(255,255,255,0.65) 50%, transparent 100%);
    }
    ${sel} .dy-flare-v {
      width: 1px; height: 100%;
      transform: translate(-50%, -50%);
      background: linear-gradient(to bottom,
        transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%);
    }
    @media (prefers-reduced-motion: reduce) {
      ${sel} .dy-sphere,
      ${sel} .dy-core, ${sel} .dy-core-hot, ${sel} .dy-halo,
      ${sel} .dy-orbit--0, ${sel} .dy-orbit--1, ${sel} .dy-orbit--2 {
        animation: none !important;
      }
      ${sel} .dy-sphere {
        transform: rotateY(20deg) rotateX(-12deg);
      }
    }
  `;
}
